/* eslint-disable react-refresh/only-export-components -- provedor, viewport e hook
   moram no mesmo arquivo de propósito: a fila é estado interno do provedor e
   separar o hook só criaria um import extra em toda página. */
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type VarianteToast = 'success' | 'error' | 'info';

export interface ToastOpcoes {
  titulo: string;
  descricao?: string;
  variante?: VarianteToast;
  /** Tempo até sumir sozinho. `0` mantém aberto até alguém fechar. */
  duracaoMs?: number;
}

export interface ContextoToast {
  toast: (opts: ToastOpcoes) => void;
}

interface ToastEmFila extends ToastOpcoes {
  id: string;
  aberto: boolean;
}

const VARIANTES: Record<VarianteToast, { caixa: string; icone: string; Icone: typeof Info }> = {
  success: {
    caixa: 'border-emerald-200 bg-white',
    icone: 'text-emerald-600',
    Icone: CircleCheck,
  },
  error: {
    caixa: 'border-red-200 bg-white',
    icone: 'text-red-600',
    Icone: CircleAlert,
  },
  info: {
    caixa: 'border-slate-200 bg-white',
    icone: 'text-santa-green',
    Icone: Info,
  },
};

const Contexto = createContext<ContextoToast | null>(null);

/** Contador de módulo: `useId` não serve porque o id nasce no clique, não no render. */
let sequencia = 0;

export function useToast(): ContextoToast {
  const ctx = useContext(Contexto);
  if (!ctx) {
    throw new Error('useToast precisa estar dentro de <ToastProvider>.');
  }
  return ctx;
}

export type ToastViewportProps = React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>;

const ToastViewport = forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Viewport>,
  ToastViewportProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 right-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 outline-none sm:max-w-sm',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = 'ToastViewport';

export interface ToastProviderProps {
  children: React.ReactNode;
  /** Padrão de 5s — tempo de ler duas linhas sem pressa. */
  duracaoPadraoMs?: number;
  /** Renderiza o viewport padrão. Desligue só para posicionar o seu. */
  renderizarViewport?: boolean;
  /** Quantos avisos podem coexistir; o mais antigo sai primeiro. */
  maximo?: number;
  className?: string;
}

export function ToastProvider({
  children,
  duracaoPadraoMs = 5000,
  renderizarViewport = true,
  maximo = 3,
  className,
}: ToastProviderProps) {
  const [fila, setFila] = useState<ToastEmFila[]>([]);
  const temporizadores = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const pendentes = temporizadores.current;
    return () => {
      pendentes.forEach(clearTimeout);
    };
  }, []);

  const toast = useCallback(
    (opts: ToastOpcoes) => {
      sequencia += 1;
      const novo: ToastEmFila = {
        id: `toast-${sequencia}`,
        aberto: true,
        variante: 'info',
        duracaoMs: duracaoPadraoMs,
        ...opts,
      };
      setFila((atual) => [...atual, novo].slice(-maximo));
    },
    [duracaoPadraoMs, maximo],
  );

  // O item não sai da lista no mesmo instante em que fecha: sem isso o Radix
  // desmonta antes da animação de saída e o aviso pisca.
  const fechar = useCallback((id: string) => {
    setFila((atual) => atual.map((t) => (t.id === id ? { ...t, aberto: false } : t)));
    const timer = setTimeout(() => {
      setFila((atual) => atual.filter((t) => t.id !== id));
    }, 250);
    temporizadores.current.push(timer);
  }, []);

  const valor = useMemo<ContextoToast>(() => ({ toast }), [toast]);

  return (
    <Contexto.Provider value={valor}>
      <ToastPrimitive.Provider swipeDirection="right" duration={duracaoPadraoMs}>
        {children}

        {fila.map((item) => {
          const v = VARIANTES[item.variante ?? 'info'];
          const Icone = v.Icone;
          return (
            <ToastPrimitive.Root
              key={item.id}
              open={item.aberto}
              duration={item.duracaoMs === 0 ? Number.POSITIVE_INFINITY : item.duracaoMs}
              onOpenChange={(aberto) => {
                if (!aberto) fechar(item.id);
              }}
              className={cn(
                'flex items-start gap-3 rounded-xl border p-4 shadow-premium-hover',
                'data-[state=open]:animate-toast-in data-[state=closed]:animate-overlay-out',
                'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
                v.caixa,
              )}
            >
              <Icone className={cn('mt-0.5 h-5 w-5 shrink-0', v.icone)} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-sm font-semibold text-santa-dark">
                  {item.titulo}
                </ToastPrimitive.Title>
                {item.descricao ? (
                  <ToastPrimitive.Description className="mt-0.5 text-sm leading-relaxed text-slate-600">
                    {item.descricao}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              <ToastPrimitive.Close
                aria-label="Fechar aviso"
                className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-santa-green"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}

        {renderizarViewport ? <ToastViewport className={className} /> : null}
      </ToastPrimitive.Provider>
    </Contexto.Provider>
  );
}

ToastProvider.displayName = 'ToastProvider';

export { ToastViewport };
